/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { NavigableBreadcrumbLink } from "../../components/page_breadcrumbs/navigable_breadcrumb_link";
import { StyledBreadCrumbContainer, StyledLink } from "../../components/navigable_breadcrumb_link/styles";
import {
  RestaurantGroupContainer,
  StyledAddButton,
  StyledButtonContainer,
  StyledCancelButton,
  StyledCreateButton,
  StyledCreateTitle,
  StyledFormContainer,
  StyledFormFieldText,
  StyledTextField,
  StyledTitle,
  StyledTitleContainer
} from "../restaurant_groups/styles";
import {
  Alert,
  Box,
  Breadcrumbs,
  Divider,
  Drawer,
  FormControl,
  FormHelperText,
  InputAdornment,
  Paper,
  Select,
  Typography
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import { strictCanScope } from "../../utils/caslStrict";
import { AbilityContext } from "../../contexts/abilityContext";
import { LoadingSpinner } from "../../components/loading_spinner";
import { StyledSelect } from "../network_guests/styles";
import { DropdownInfiniteLoader } from "../../components/dropdown_infinite_loader";
import { SelectedMenuItem } from "../../components/selected_menu_item";
import { StyledMenuItem } from "../../components/styled_menu_item/index.jsx";
import api from "../../api/index.js";
import authenticatedFetch from "../../api/authenticatedFetch";
import { keepPreviousData, useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import DropdownSearchBar from "../../components/dorpdown_searchbar/index.jsx";
import { useNotificationContext } from "../../contexts/notificationContext";
import { Loader } from "../../components/loader/index.jsx";
import { IndexTable } from "../../components/index_table/index.jsx";
import camelize from "../../utils/camelize.js";
import { formatDate } from "../../utils/formatDate.js";
import { FilterContainer } from "../../components/filter_container/index.jsx";
import { selectBaseStyles, viewTypographyStyles } from "../reports/styles.js";
import { StyledInputLabel } from "../restaurant/styles.jsx";
import { UpdateButton } from "../../components/update_button/index.jsx";
import { StyledBox, StyledSearchBar } from "../reports/global_employee_search/styles.jsx";
import SearchIcon from '@mui/icons-material/Search';
import { useDebounce } from "../../utils/useDebounce.js";
import { StyledNameLink } from "../reports/mobile_user_activity/styles.jsx";
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import { useLocation, useNavigate } from "react-router-dom";
import { useOtMutation } from "../../hooks/mutations/useOtMutation";
import { employeeGroupMutationKeyGroups } from "../../hooks/mutations/queryKeyGroups/employeeGroupMutationKeys";
import { Query_Keys } from "../../lib/queryKeys.js";
import { buildQueryParams } from "../../utils/buildQueryParams.js";
import { useParentContextHydration } from "../../hooks/useParentContextHydration.js";
import { PerPage } from "../../constants/perPage.js";
import {
  getDropdownNextPageParam,
  mergeUniqueByKey,
  normalizeRestaurantGroup,
} from "../../utils/dropdownInfiniteQuery.js";
import { useInfiniteScrollObserver } from "../../hooks/useInfiniteScrollObserver";

const drawerWidth = 470

export const EmployeeGroups = () => {
  const { ability } = useContext(AbilityContext)
  const location = useLocation()
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false)
  const defaultCreatePermission = strictCanScope(ability, 'create_restaurant', 'Restaurant_Group');
  const singleRestaurantGroupAutoApplied = useRef(false)
  const [loading, setLoading] = useState(false)
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [errors, setErrors] = useState({})
  const [createInstancePermission, setCreateInstancePermission] = useState(false)
  const [restaurantGroupSearch, setRestaurantGroupSearch] = useState('')
  const [restaurantGroupOptionsCache, setRestaurantGroupOptionsCache] = useState([])
  const [filters, setFilters] = useState({})
  const [appliedFilters, setAppliedFilters] = useState({})
  const [createRestaurantGroupSearch, setCreateRestaurantGroupSearch] = useState('')
  const [createRestaurantGroupOptionsCache, setCreateRestaurantGroupOptionsCache] = useState([])
  const [prefillRestaurantGroupName, setPrefillRestaurantGroupName] = useState(location?.state?.restaurant_group_name || '')
  const [search, setSearch] = useState('')
  const showNotification = useNotificationContext();
  const navigate = useNavigate();
  const [filtersChanged, setFiltersChanged] = useState(false)
  const debouncedSearchTerm = useDebounce(search, 300);
  const queryClient = useQueryClient();
  const [newEmployeeGroup, setNewEmployeeGroup] = useState({
    name: '',
    restaurant_group_id: ''
  })
  const [csvLoading, setCsvLoading] = useState(false)
  const debouncedRestaurantGroupSearch = useDebounce(restaurantGroupSearch.trim(), 300)
  const debouncedCreateRestaurantGroupSearch = useDebounce(createRestaurantGroupSearch.trim(), 300)

  useParentContextHydration({
    ability,
    setters: {
      setFilters,
      setAppliedFilters,
      setSearch,
      setPage,
      setItemsPerPage,
      setSortBy,
      setSortOrder,
      // The query is no longer gated by a manually-flipped "apply" toggle (see
      // `hasAppliedRestaurantGroup` below), but useParentContextHydration still
      // expects a setter here, so hand it a no-op.
      setApplyFilters: () => {},
    },
    defaultSortBy: "",
    defaultSortOrder: "desc",
    permissionResolver: (parentCtx, currentAbility) => {
      if (parentCtx.filters?.restaurant_group_id) {
        setCreateInstancePermission(strictCanScope(currentAbility, 'create_restaurant', 'Restaurant_Group', `${parentCtx.filters.restaurant_group_id}`));
      }
    },
    legacyHandler: (currentAbility) => {
      if (location?.state?.restaurant_group_id) {
        setFilters(prev => ({ ...prev, restaurant_group_id: location.state.restaurant_group_id }))
        setCreateInstancePermission(strictCanScope(currentAbility, 'create_restaurant', 'Restaurant_Group', `${location.state.restaurant_group_id}`))
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  });

  const fetchRestaurantGroupList = useCallback(async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/restaurant_groups_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantGroupSearch || undefined,
      },
    });
    return response?.data?.map(normalizeRestaurantGroup) || [];
  }, [debouncedRestaurantGroupSearch])

  const {
    data: restaurantGroups = [],
    isFetching: restaurantGroupFetching,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_groups_dropdown({ search: debouncedRestaurantGroupSearch }),
    queryFn: fetchRestaurantGroupList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const fetchCreateRestaurantGroupList = useCallback(async ({ pageParam = 1 }) => {
    const response = await api.get('/v1/admin/dropdowns/permission_restaurant_group_list', {
      params: {
        resource_action: 'create_restaurant',
        page: pageParam,
        per_page: PerPage,
        search: debouncedCreateRestaurantGroupSearch || undefined,
      }
    });
    return response?.data || [];
  }, [debouncedCreateRestaurantGroupSearch])

  const {
    data: createRestaurantGroupsList = [],
    isFetching: createRestaurantGroupFetching,
    isFetchingNextPage: createRestaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextCreateRestaurantGroupsPage,
    hasNextPage: hasNextCreateRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.permission_restaurant_group({
      resource_action: 'create_restaurant',
      search: debouncedCreateRestaurantGroupSearch,
    }),
    queryFn: fetchCreateRestaurantGroupList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { rootRef: restaurantGroupMenuRef, targetRef: restaurantGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
  })

  const { rootRef: createRestaurantGroupMenuRef, targetRef: createRestaurantGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextCreateRestaurantGroupsPage,
    hasNextPage: hasNextCreateRestaurantGroupsPage,
    isFetchingNextPage: createRestaurantGroupsFetchingNextPage,
  })

  const { mutateAsync: createEmployeeGroupMutation } = useOtMutation({
    mutationFn: async (payload) => await api.post("/v1/admin/employee_groups", payload),
    invalidateKeys: [...employeeGroupMutationKeyGroups.create],
    successMessage: () => "Employee Group has been created successfully",
    onSuccess: () => {
      handleDrawerClose();
    },
    onError: (error) => {
      setErrors(error?.response?.data?.errors || {});
    },
    onSettled: () => setLoading(false)
  });

  const fetchEmployeeGroups = useCallback(async () => {
    const response = await api.get('/v1/admin/employee_groups', {
      params: {
        per_page: itemsPerPage,
        page: page,
        restaurant_group_id: appliedFilters?.restaurant_group_id,
        search: debouncedSearchTerm,
        ...(sortBy ? { sort_by: sortBy, sort_order: sortOrder } : {}),
      }
    })
    return { data: response?.data, pageCount: response?.headers["total-count"] }
  }, [itemsPerPage, page, debouncedSearchTerm, appliedFilters?.restaurant_group_id, sortBy, sortOrder])

  const hasAppliedRestaurantGroup = !!appliedFilters?.restaurant_group_id

  const { data: employeeGroups, isLoading, isFetching } = useQuery({
    queryKey: Query_Keys.employee_groups({
      queryFilter: appliedFilters,
      page: page,
      itemsPerPage: itemsPerPage,
      search: debouncedSearchTerm,
      sortBy,
      sortOrder,
    }),
    queryFn: fetchEmployeeGroups,
    staleTime: 10 * 60 * 1000,
    enabled: hasAppliedRestaurantGroup,
    placeholderData: keepPreviousData,
  })

  useEffect(() => {
    setRestaurantGroupOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, restaurantGroups, 'label'))
  }, [restaurantGroups])

  useEffect(() => {
    setCreateRestaurantGroupOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, createRestaurantGroupsList, 'value'))
  }, [createRestaurantGroupsList])

  useEffect(() => {
    if (!filters?.restaurant_group_id || !prefillRestaurantGroupName) {
      return
    }

    const hasMatchingRestaurantGroup = restaurantGroupOptionsCache?.some(
      (restaurantGroup) => String(restaurantGroup?.label) === String(filters?.restaurant_group_id),
    )

    if (!hasMatchingRestaurantGroup && !restaurantGroupSearch) {
      setRestaurantGroupSearch(prefillRestaurantGroupName)
      return
    }

    if (hasMatchingRestaurantGroup && restaurantGroupSearch === prefillRestaurantGroupName) {
      setRestaurantGroupSearch('')
      setPrefillRestaurantGroupName('')
    }
  }, [filters?.restaurant_group_id, prefillRestaurantGroupName, restaurantGroupOptionsCache, restaurantGroupSearch])

  const hasSingleAvailableRestaurantGroup = !debouncedRestaurantGroupSearch && restaurantGroups.length === 1

  useEffect(() => {
    if (filters?.restaurant_group_id || singleRestaurantGroupAutoApplied.current || !hasSingleAvailableRestaurantGroup) {
      return
    }

    const singleRestaurantGroup = restaurantGroups?.[0]
    const nextFilters = { restaurant_group_id: singleRestaurantGroup?.label }

    singleRestaurantGroupAutoApplied.current = true
    setFilters(nextFilters)
    setAppliedFilters(nextFilters)
    setCreateInstancePermission(strictCanScope(ability, 'create_restaurant', 'Restaurant_Group', `${singleRestaurantGroup?.label}`))
  }, [ability, filters?.restaurant_group_id, hasSingleAvailableRestaurantGroup, restaurantGroups])

  const getRestaurantGroupName = (id) => {
    const restaurantGroup = restaurantGroupOptionsCache?.find((res) => res.label === id)
    return restaurantGroup?.value ? restaurantGroup?.value : prefillRestaurantGroupName
  }

  const getCreateRestaurantGroupName = (id) => {
    const restaurantGroup = createRestaurantGroupOptionsCache?.find((res) => res.value === id)
    return restaurantGroup ? restaurantGroup?.label : ''
  }

  const handleDrawerClose = () => {
    setCreateDrawerOpen(false)
    setNewEmployeeGroup({
      name: '',
      restaurant_group_id: ''
    })
    setErrors({});
  }

  const handleInputChange = (value, field) => {
    setNewEmployeeGroup({ ...newEmployeeGroup, [field]: value })
  }

  const handleSearch = (searchQuery) => {
    if (filters?.restaurant_group_id && employeeGroups?.data) {
      setSearch(searchQuery)
      setPage(1)
    }
  }

  const handleSortChange = useCallback((nextSortBy, nextSortOrder) => {
    setSortBy(nextSortBy)
    setSortOrder(nextSortOrder)
    setPage(1)
  }, [])

  const handleExportCsv = useCallback(async () => {
    try {
      setCsvLoading(true)
      const exportParams = {
        restaurant_group_id: appliedFilters?.restaurant_group_id,
        search: debouncedSearchTerm,
        ...(sortBy ? { sort_by: sortBy, sort_order: sortOrder } : {}),
      }
      const query = buildQueryParams(exportParams)
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?report=employee_groups&${query}`

      const { response } = await authenticatedFetch(url, {
        method: 'GET',
        defaultErrorMessage: 'Failed to download CSV'
      })

      const blob = await response.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = downloadUrl
      const date = new Date().toISOString().slice(0, 10)
      a.download = `employee_groups_${date}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(downloadUrl)
      showNotification('CSV downloaded successfully', 'success')
    } catch (error) {
      console.error('CSV download error:', error)
      showNotification(error?.message || 'Failed to download CSV', 'error')
    } finally {
      setCsvLoading(false)
    }
  }, [appliedFilters, debouncedSearchTerm, sortBy, sortOrder])

  const handleAdd = async () => {
    if (!newEmployeeGroup?.restaurant_group_id) {
      setErrors(prev => ({ ...prev, restaurant_group_id: 'Please select a restaurant group' }))
      return
    }
    setLoading(true);

    try {
      await createEmployeeGroupMutation(newEmployeeGroup);
    } catch {
      // Error is handled in the mutation onError callback
    }
  }

  const handleRestaurantGroupChange = (event) => {
    setFilters(prev => ({ ...prev, restaurant_group_id: event.target.value }))
    setPrefillRestaurantGroupName('')
    setCreateInstancePermission(strictCanScope(ability, 'create_restaurant', 'Restaurant_Group', `${event.target.value}`))
    setFiltersChanged(true)
  }

  const handleChange = () => {
    if (!filters?.restaurant_group_id) {
      showNotification('Please select a restaurant group', 'error')
      return
    }

    setAppliedFilters(filters)
    setFiltersChanged(false)
    setItemsPerPage(25);
    setPage(1);
  }

  const TableColumns = [
    { key: "group_name", sortable: true },
    { key: "group_id", sortable: true },
    { key: "members", sortable: true },
    { key: "created_at", sortable: true },
  ]

  const columns = TableColumns?.map((column) => {
    return {
      key: column.key,
      label: camelize(column.key),
      sortable: column.sortable,
    }
  })

  const TableRows = (data) => {
    return data?.map((record) => {
      return {
        group_name: record?.name,
        group_id: record?.group_id,
        members: {
          display: (
            <div style={{ display: 'flex', alignItems: 'center' }}>
            <PeopleAltOutlinedIcon sx={{ color: '#18181B' }} />
            <StyledNameLink
              data-testid="members-link"
              style={{ minWidth: '35px' }}
              onClick={() => navigate(`/admin/employee_groups/${record?.id}/group_members`, { 
                state: { 
                  restaurant_group_id: record?.restaurant_group_id,
                  restaurant_group_name: getRestaurantGroupName(record?.restaurant_group_id),
                  parentContext: { appliedFilters, filters, search, itemsPerPage, page, sortBy, sortOrder }
                }
              })}
            >
              {record?.group_members?.length}
            </StyledNameLink>
            </div>
          ),
          sortValue: record?.group_members?.length
        },
        created_at: formatDate(record?.created_at, record?.time_zone_abbr)
      }
    })
  }

  const rows = TableRows(employeeGroups?.data);

  return (
    <RestaurantGroupContainer
      style={{
        width: createDrawerOpen ? `calc(100% - ${drawerWidth}px)` : '100%'
      }}
    >
      <StyledBreadCrumbContainer>
        <Breadcrumbs>
          <NavigableBreadcrumbLink 
            LinkComponent={StyledLink} 
            label="Home" to="/admin/dashboard" 
          />
          <StyledLink color={'#515152'}>
            Employee Groups
          </StyledLink>
        </Breadcrumbs>
      </StyledBreadCrumbContainer>

      <FilterContainer>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", gap: "18px" }}>
              <Typography
                variant="body2"
                sx={{
                  ...viewTypographyStyles,
                  alignSelf: { xs: "flex-start", sm: "center" },
                  whiteSpace: "nowrap",
                  mt: 0
                }}
              >
                View
              </Typography>

              <FormControl fullWidth size="small">
                <StyledInputLabel>Restaurant Group</StyledInputLabel>
                <Select
                  size="small"
                  label="Restaurant Group"
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PaperProps: { ref: restaurantGroupMenuRef, sx: { maxHeight: "40vh" } },
                    autoFocus: false,
                    MenuListProps: { sx: { pt: 0 } },
                  }}
                  sx={{
                    ...selectBaseStyles,
                    minWidth: 160,
                    borderColor: "#2b4fb9",
                  }}
                  onChange={handleRestaurantGroupChange}
                  onClose={() => setTimeout(() => setRestaurantGroupSearch(''), 1000)}
                  value={filters?.restaurant_group_id || ""}
                  renderValue={() =>
                    getRestaurantGroupName(filters?.restaurant_group_id)
                  }
                >
                  <DropdownSearchBar changeSearchText={setRestaurantGroupSearch} placeholder={'Search restaurant group'} isFetching={restaurantGroupFetching} />
                  {restaurantGroups?.map((res) => (
                    <StyledMenuItem
                      key={res?.label}
                      value={res?.label}
                    >
                      {
                        filters?.restaurant_group_id === res?.label ? <SelectedMenuItem data={res?.value} /> : res?.value
                      }
                    </StyledMenuItem>
                  ))}
                  <DropdownInfiniteLoader
                    hasNextPage={hasNextRestaurantGroupsPage}
                    targetRef={restaurantGroupLoaderRef}
                  />
                </Select>
              </FormControl>
            </Box>

            <UpdateButton
              onClick={handleChange}
              filtersChanged={filtersChanged}
              status={isFetching}
            />
          </Box>
        </FilterContainer>

      <StyledBox report={false}>
        <StyledTitleContainer>
          <StyledTitle>Employee Groups</StyledTitle>
        </StyledTitleContainer>

        <StyledSearchBar
          value={search}
          placeholder="Search groups"
          InputProps={{
            startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>),
          }}
          onChange={(e) => handleSearch(e.target.value)}
          autoComplete="off"
        />
      </StyledBox>

      <Divider style={{ borderStyle: 'solid', borderWidth: '1px' }} />

      <div style={{ padding: '20px 0px' }}>
        <StyledCreateButton
          variant="contained"
          startIcon={<AddIcon/>}
          onClick={() => setCreateDrawerOpen(true)}
          disabled={!(defaultCreatePermission || createInstancePermission)}
        >
          Create Employee Group
        </StyledCreateButton>
      </div>

      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            marginTop: 'calc(35px * 2)',
          },
        }}
        variant="persistent"
        anchor="right"
        open={createDrawerOpen}
        data-testid="employee-group-create-drawer"
      >
        <div style={{padding: '0px 15px'}}>
          <StyledCreateTitle>Create Employee Group</StyledCreateTitle>

          <StyledFormContainer>
          <div style={{ marginTop: '3%' }}>
            <StyledFormFieldText>Restaurant group<span style={{color: 'red'}}>*</span></StyledFormFieldText>
              <Box>
                <StyledSelect
                  value={newEmployeeGroup?.restaurant_group_id}
                  onChange={(e) => {
                    handleInputChange(e.target.value, "restaurant_group_id")
                    setErrors(prev => ({ ...prev, restaurant_group_id: null }))
                  }}
                  onClose={() => setTimeout(() => setCreateRestaurantGroupSearch(''), 1000)}
                  MenuProps={{
                    PaperProps: {
                      ref: createRestaurantGroupMenuRef,
                      sx: { maxHeight: 400 },
                    },
                    MenuListProps: { sx: { pt: 0 } },
                  }}
                  renderValue={() => getCreateRestaurantGroupName(newEmployeeGroup?.restaurant_group_id)}
                  error={!!errors?.restaurant_group_id}
                  data-testid="restaurant-group-select"
                >
                  <DropdownSearchBar changeSearchText={setCreateRestaurantGroupSearch} placeholder={'Search restaurant group'} isFetching={createRestaurantGroupFetching} />
                  {createRestaurantGroupsList?.map((res) => (
                    <StyledMenuItem
                      key={res?.value}
                      value={res?.value}
                    >
                      {newEmployeeGroup?.restaurant_group_id === res?.value ? <SelectedMenuItem data={res?.label} /> : res?.label}
                    </StyledMenuItem>
                  ))}
                  <DropdownInfiniteLoader
                    hasNextPage={hasNextCreateRestaurantGroupsPage}
                    targetRef={createRestaurantGroupLoaderRef}
                  />
                </StyledSelect>
                {!!errors?.restaurant_group_id && (
                  <FormHelperText
                    sx={{ color: "#d32f2f", marginLeft: "14px" }}
                  >
                    Please select a restaurant group
                  </FormHelperText>
                )}
              </Box>
            </div>

            <div style={{marginTop: '3%'}}>
              <StyledFormFieldText>Employee group name<span style={{color: 'red'}}>*</span></StyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                onChange={(e) => {
                  handleInputChange(e.target.value, "name")
                  setErrors(prev => ({ ...prev, name: null }))
                }}
                error={errors?.name}
                helperText={errors?.name}
                value={newEmployeeGroup?.name}
                data-testid="employee-group-name-input"
              />
            </div>
          </StyledFormContainer>
        </div>

        <Paper
          sx={{
            position: 'fixed',
            bottom: 0,
            right: 0,
            width: drawerWidth,
            height: '70px'
          }}
          elevation={2}
        >
          <StyledButtonContainer>
            <StyledCancelButton
              onClick={handleDrawerClose}
              variant="outlined"
            >
              Cancel
            </StyledCancelButton>
            <StyledAddButton
              onClick={handleAdd}
              variant='contained'
              disabled={loading}
            >
              {loading ? <LoadingSpinner isLoading={true} /> : 'Create'}
            </StyledAddButton>
          </StyledButtonContainer>
        </Paper>
      </Drawer>

      {(isFetching || isLoading) && !search ? (
          <Box data-testid="loader" >
            <Loader />
          </Box>
        ) : employeeGroups?.data && filters?.restaurant_group_id ? (
          <IndexTable
            columns={columns}
            data={rows}
            tableId={"employee-groups"}
            type={"entries"}
            sortMode="server"
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            page={page}
            setPage={setPage}
            pageCount={employeeGroups?.pageCount}
            searchLoading={isFetching && !!debouncedSearchTerm}
            hasSearchTerm={!!debouncedSearchTerm}
            report={false}
            showDownloadButton={true}
            disableDownloadButton={!employeeGroups?.data?.length || csvLoading}
            handleExportCsv={handleExportCsv}
            csvLoading={csvLoading}
          />
        ) : (
          <div>
            <Alert severity="info">
              Select a restaurant group
            </Alert>
          </div>
        )}
    </RestaurantGroupContainer>
  )
}
